import {isUndefined} from 'lodash';
import React, {ReactNode} from 'react';

import {AppRoutesPrefixesEnum} from '../components/appRoutesPrefixes';
import {ChannelTypesEnum} from '../models/api/channelCommonTypes';
import {SettingsMatchProps} from '../models/url/settingsUrlModel';
import {DataState} from '../store/data/dataState';
import {FeatureFlagsEnum} from '../store/featureFlags/featureFlagsTypes';
import {SettingsPanelId} from '../store/panels/panelStateBase';
import {RuleComplexRecipeTypesEnum} from './rules/ruleRecipeModel';
import {SettingsContainer} from './settingsContainer';

export interface SettingsRoute {
  path: string;
  render: (panelId: SettingsPanelId, matchProps: SettingsMatchProps) => ReactNode;
  featureFlag?: FeatureFlagsEnum;
  // This is a temporary addition for the inboxes migration project
  canBeRendered?: (matchProps: SettingsMatchProps, dataState: DataState) => boolean;
}

const tagsRoutes = [
  ':namespace/tags/:tagId/new',
  ':namespace/tags/:tagId/edit',
  ':namespace/tags/new',
  ':namespace/tags/:tagId/overview?page&search',
  ':namespace/tags/overview?page&search',
  ':namespace/tags/:tagId?page',
  ':namespace/tags?page&search',
];

const settingsTagsRoutes: ReadonlyArray<SettingsRoute> = [...tagsRoutes].map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const ruleLibraryRoutes = [
  // ':namespace/rules',
  ':namespace/rules/library',
];

const settingsRuleLibraryRoutes: ReadonlyArray<SettingsRoute> = ruleLibraryRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const settingsRuleListRoutes: SettingsRoute = {
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/:namespace/rules?query&categories&only_smart&search&inbox`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
};

const ruleEditorRoutes = [
  ':namespace/rules/new',
  ':namespace/rules/new/:recipe',
  ':namespace/rules/edit/:ruleId',
  ':namespace/inboxes/new/inbox/rules/new/:recipe?inbox',
  ':namespace/inboxes/new/inbox/rules/edit/:ruleId',
  // Order here matters, since `recipes` doesn't implement `edit` and `new` prefix
  // @RULES TODO: SLA support
  // ':namespace/rules/recipes/:recipe',
  // ':namespace/rules/recipes'
];

const settingsRuleEditorRoutes: ReadonlyArray<SettingsRoute> = ruleEditorRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

/**
 * TODO: Merge this with ruleEditorRoutes once all recipes has
 * been migrated to front-client.
 */
const requiredTaggingEditorRoutes = [
  ':namespace/rules/recipes/required-tagging',
  ':namespace/rules/recipes/required-tagging/:bundleId',
];

const settingsRequiredTaggingEditorRoutes: ReadonlyArray<SettingsRoute> = requiredTaggingEditorRoutes.map(
  (path) => ({
    path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
    render: (panelId, matchProps) => (
      <SettingsContainer
        panelId={panelId}
        matchProps={mergeMatchPropsWithParams(matchProps, {
          recipe: RuleComplexRecipeTypesEnum.REQUIRED_TAGGING,
        })}
      />
    ),
  }),
);

const slaEditorRoutes = [':namespace/rules/recipes/sla', ':namespace/rules/recipes/:bundleId'];

const settingsSlaEditorRoutes: ReadonlyArray<SettingsRoute> = slaEditorRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => (
    <SettingsContainer
      panelId={panelId}
      matchProps={mergeMatchPropsWithParams(matchProps, {
        recipe: RuleComplexRecipeTypesEnum.SLA,
      })}
    />
  ),
}));

const csatEditorRoutes = [':namespace/rules/recipes/csat/new', ':namespace/rules/recipes/csat/:bundleId'];

const settingsCsatEditorRoutes: ReadonlyArray<SettingsRoute> = csatEditorRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => (
    <SettingsContainer
      panelId={panelId}
      matchProps={mergeMatchPropsWithParams(matchProps, {
        recipe: RuleComplexRecipeTypesEnum.CSAT,
      })}
    />
  ),
}));

const customFieldsRoutes = [
  ':namespace/custom-fields/:tabPage/overview',
  ':namespace/custom-fields/:tabPage/new',
  ':namespace/custom-fields/:tabPage/edit/:customFieldId',
];

const settingsCustomFieldsRoutes: ReadonlyArray<SettingsRoute> = customFieldsRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const signaturesRoutes = [
  ':namespace/signatures',
  ':namespace/signatures/overview',
  ':namespace/signatures/new',
  ':namespace/signatures/edit/:signatureId',
  // Need the two routes below so we don't show the legacy UI when navigating to them
  ':namespace/signatures/edit/:signatureId/overview',
  ':namespace/signatures/edit/:signatureId/access',
];

const settingsSignaturesRoutes: ReadonlyArray<SettingsRoute> = signaturesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const answersRoutes = [
  ':namespace/answers',
  ':namespace/answers/new',
  ':namespace/answers/overview',
  ':namespace/answers/edit/:answerId/overview',
  ':namespace/answers/folders/:folderId/edit',
  ':namespace/answers/folders/:folderId/new',
  ':namespace/answers/folders/new',
  ':namespace/answers/folders/:folderId/overview',
];

const settingsAnswersRoutes: ReadonlyArray<SettingsRoute> = answersRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const inboxesRoutes = [
  ':namespace/inboxes/import-custom-attributes',
  ':namespace/inboxes/edit/:inboxId/business-hours',
];

const settingsInboxesRoutes: ReadonlyArray<SettingsRoute> = inboxesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const gatedInboxesRoutes = [
  ':namespace/inboxes',
  ':namespace/inboxes/edit/:inboxId/overview',
  ':namespace/inboxes/edit/:inboxId/settings',
  ':namespace/inboxes/edit/:inboxId/channels/:channelId',
  ':namespace/inboxes/edit/:inboxId/channels/list',
];

const migratedChannelTypes: ReadonlyArray<ChannelTypesEnum> = [
  ChannelTypesEnum.FRONT_CHAT,
  ChannelTypesEnum.GMAIL,
  ChannelTypesEnum.OFFICE365,
  ChannelTypesEnum.SMTP,
  ChannelTypesEnum.CUSTOM,
  ChannelTypesEnum.TWILIO,
  ChannelTypesEnum.SMOOCH,
  ChannelTypesEnum.TWITTER,
  ChannelTypesEnum.TWITTER_DM,
];

const gatedSettingsInboxesRoutes: ReadonlyArray<SettingsRoute> = gatedInboxesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
  featureFlag: FeatureFlagsEnum.INBOXES_FRONT_CLIENT,
  canBeRendered: (matchProps, dataState) => {
    const maybeNumberChannelId = Number(matchProps.params?.channelId);

    if (matchProps.params?.channelId === 'list') {
      return true;
    }

    if (!isUndefined(matchProps.params?.channelId)) {
      if (Number.isNaN(maybeNumberChannelId)) {
        return false;
      }

      const existingChannel = dataState.channels.byId[maybeNumberChannelId];
      const isMigrated = existingChannel && migratedChannelTypes.includes(existingChannel.type);

      return Boolean(existingChannel && isMigrated);
    }

    return true;
  },
}));

const teammatesRoutes = [
  ':namespace/teammates/edit/:teammateAlias/rules/new',
  ':namespace/teammates/edit/:teammateAlias/rules/edit/:ruleId',
  ':namespace/teammates/import-custom-attributes',
];

const settingsTeammatesRoutes: ReadonlyArray<SettingsRoute> = teammatesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const preferencesRoutes = [':namespace/user-preferences?search'];

const settingsPreferencesRoutes: ReadonlyArray<SettingsRoute> = preferencesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const snoozesRoutes = [
  ':namespace/snoozes',
  ':namespace/snoozes/new',
  ':namespace/snoozes/overview',
  ':namespace/snoozes/edit/:snoozeId',
];

const settingsSnoozesRoutes: ReadonlyArray<SettingsRoute> = snoozesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const vacationResponderRoutes = [
  ':namespace/responders',
  ':namespace/responders/new',
  ':namespace/responders/overview',
  ':namespace/responders/edit/:vacationResponderId',
];

const settingsVacationRespondersRoutes: ReadonlyArray<SettingsRoute> = vacationResponderRoutes.map(
  (path) => ({
    path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
    render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
    featureFlag: FeatureFlagsEnum.NEW_VACATION_RESPONDERS,
  }),
);

const applicationsRoutes = [
  'applications?q',
  'applications/:page(new)',
  'applications/:applicationId/:component(details|oauth_client)',
  'applications/:applicationId/:component(details|oauth_client)/:page(edit)',
  'applications/:applicationId/:component(autolinkers|previewers|servers)?q',
  'applications/:applicationId/:component(autolinkers|previewers|servers)/:page(edit)/:id',
  'applications/:applicationId/:component(autolinkers|previewers|servers|oauth_client)/:page(new)',
  'applications/:applicationId/:component(autolinkers|previewers|servers)/:id/:page(details|edit)',
];

const settingsApplicationsRoutes: ReadonlyArray<SettingsRoute> = applicationsRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

// Circles only lives in the global namespace for now.
const circlesRoutes = ['global/circles', 'global/circles/edit/:circleId', 'global/circles/new'];

const settingsCirclesRoutes: ReadonlyArray<SettingsRoute> = circlesRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
  featureFlag: FeatureFlagsEnum.CIRCLES,
}));

const baseSettingsRoute: SettingsRoute = {
  path: `/${AppRoutesPrefixesEnum.SETTINGS}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
};

const homeRoutes = ['home', 'user-home'];
const settingsHomeRoutes: ReadonlyArray<SettingsRoute> = homeRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

const settingsProfileRoutes: SettingsRoute = {
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/profile`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
};

/*
 * Apps Routes.
 */

const appsRoutes = [
  'apps?q&prev&source',
  'apps/:selectedTab(collections|categories|popular|plugins|channels|enabled|all)?q&prev&source',
  'apps/:selectedTab(categories)/:category?q&prev',
  'apps/:selectedTab(details)/:appId?prev',
  'apps/:selectedTab(details)/:appId/:page(overview|getting-started)?prev&open_configuration',
];

const settingsAppsRoutes: ReadonlyArray<SettingsRoute> = appsRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
  featureFlag: FeatureFlagsEnum.NEW_INTEGRATIONS_DISCOVERABILITY,
}));

/*
 * Notifications Routes.
 */

const notificationsRoutes = [
  'notifications',
  'notifications/advanced/edit/:notificationIndex',
  'notifications/advanced/new',
  'notifications/advanced',
];

const settingsNotificationsRoutes: ReadonlyArray<SettingsRoute> = notificationsRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
}));

/*
 * Developers Routes.
 */

const developerRoutes = [
  'developers',
  'developers/:subPage(overview|tokens)',
  'developers/:subPage(tokens)/:tokenId',
  'developers/:appId',
  'developers/:appId/:detailsPage(details|features|oauth|servers)',
  'developers/:appId/:detailsPage(features)/:feature',
  'developers/:appId/:detailsPage(servers)/:page(connect)',
  'developers/:appId/:detailsPage(servers)/:serverId',
];

const settingsDeveloperRoutes: ReadonlyArray<SettingsRoute> = developerRoutes.map((path) => ({
  path: `/${AppRoutesPrefixesEnum.SETTINGS}/${path}`,
  render: (panelId, matchProps) => <SettingsContainer panelId={panelId} matchProps={matchProps} />,
  featureFlag: FeatureFlagsEnum.DEVELOPER_SETTINGS,
}));

/*
 * Routes.
 */

export const settingsRoutes: ReadonlyArray<SettingsRoute> = [
  baseSettingsRoute,
  settingsProfileRoutes,
  ...settingsHomeRoutes,
  ...settingsTagsRoutes,
  settingsRuleListRoutes,
  ...settingsRuleLibraryRoutes,
  ...settingsRuleEditorRoutes,
  ...settingsRequiredTaggingEditorRoutes,
  ...settingsSlaEditorRoutes,
  ...settingsCsatEditorRoutes,
  ...settingsCustomFieldsRoutes,
  ...settingsSignaturesRoutes,
  ...settingsAnswersRoutes,
  ...settingsInboxesRoutes,
  ...gatedSettingsInboxesRoutes,
  ...settingsTeammatesRoutes,
  ...settingsPreferencesRoutes,
  ...settingsSnoozesRoutes,
  ...settingsApplicationsRoutes,
  ...settingsCirclesRoutes,
  ...settingsAppsRoutes,
  ...settingsVacationRespondersRoutes,
  ...settingsNotificationsRoutes,
  ...settingsDeveloperRoutes,
];

export type SettingsRouteFindResult = {
  route: SettingsRoute;
  match: SettingsMatchProps;
};

export type SettingsRouteFinder = (path: string | undefined) => SettingsRouteFindResult | undefined;

function mergeMatchPropsWithParams(matchProps: SettingsMatchProps, params: Record<string, any>) {
  return {
    ...matchProps,
    params: {
      ...matchProps.params,
      ...params,
    },
  };
}
